import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Globe, Database, Server, Headphones } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { Reveal, SectionLabel } from "@/components/Reveal";

const ICONS = { web: Globe, db: Database, infra: Server, support: Headphones };
const IMAGES = { web: "/img/engineer.jpg", db: "/img/rack.jpg", support: "/img/team.jpg" };

const ParallaxImg = ({ src, alt }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <div ref={ref} className="absolute inset-0 overflow-hidden">
      <motion.img src={src} alt={alt} style={{ y, scale: 1.2 }} className="h-full w-full object-cover grayscale-[40%] transition-[filter] duration-700 group-hover:grayscale-0" />
    </div>
  );
};

const Tile = ({ item, index, className, variant }) => {
  const Icon = ICONS[item.k];
  const img = IMAGES[item.k];
  const dark = variant !== "teal";
  return (
    <Reveal delay={index * 0.08} className={className}>
      <article
        data-testid={`service-${item.k}`}
        className={`group relative h-full min-h-[300px] overflow-hidden rounded-[28px] p-7 lg:p-9 flex flex-col justify-between transition-transform duration-500 hover:-translate-y-1 ${
          variant === "teal" ? "bg-[#2DD4BF] text-[#0B1F3A]" : "bg-[#0B1F3A] text-white"
        }`}
      >
        {img && variant === "photo" && (
          <>
            <ParallaxImg src={img} alt={item.t} />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F3A] via-[#0B1F3A]/70 to-[#0B1F3A]/10" />
          </>
        )}
        <div className="relative flex items-start justify-between">
          <span className={`grid place-items-center w-12 h-12 rounded-2xl ${dark ? "bg-white/10 text-[#2DD4BF]" : "bg-[#0B1F3A] text-[#2DD4BF]"} transition-transform duration-500 group-hover:rotate-[-8deg]`}>
            <Icon size={20} />
          </span>
          <span className={`font-mono text-xs ${dark ? "text-slate-300" : "text-[#0B1F3A]/70"}`}>0{index + 1}</span>
        </div>
        <div className="relative mt-16">
          <h3 className="text-3xl lg:text-4xl font-bold tracking-tight stretch-condensed uppercase">{item.t}</h3>
          <p className={`mt-3 max-w-md text-[15px] leading-relaxed ${dark ? "text-slate-300" : "text-[#0B1F3A]/80"}`}>{item.d}</p>
        </div>
      </article>
    </Reveal>
  );
};

export const Services = () => {
  const { t } = useLang();
  const [web, db, infra, support] = t.services.items;
  return (
    <section id="services" className="relative bg-[#EEF1EC] text-[#0B1F3A] py-24 lg:py-36" data-testid="services-section">
      <div className="max-w-[1440px] mx-auto px-5 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-14 lg:mb-20">
          <Reveal className="lg:col-span-7">
            <SectionLabel dark>{t.services.label}</SectionLabel>
            <h2 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase stretch-condensed leading-[0.92] tracking-tight">{t.services.title}</h2>
          </Reveal>
          <Reveal className="lg:col-span-4 lg:col-start-9" delay={0.1}>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed">{t.services.sub}</p>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-5 lg:auto-rows-[300px]">
          <Tile item={web} index={0} variant="photo" className="lg:col-span-7 lg:row-span-2" />
          <Tile item={db} index={1} variant="dark" className="lg:col-span-5" />
          <Tile item={infra} index={2} variant="teal" className="lg:col-span-5" />
          <Tile item={support} index={3} variant="photo" className="lg:col-span-12" />
        </div>
      </div>
    </section>
  );
};
